import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning' | 'notification';
  title: string;
  message: string;
  duration?: number; // ms, null = infinite
  actionUrl?: string;
  icon?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$ = this.toastsSubject.asObservable();
  private toastIdCounter = 0;

  /**
   * Show a success toast
   */
  success(title: string, message: string, duration = 5000): string {
    return this.addToast('success', title, message, duration);
  }

  /**
   * Show an error toast
   */
  error(title: string, message: string, duration = 7000): string {
    return this.addToast('error', title, message, duration);
  }

  /**
   * Show an info toast
   */
  info(title: string, message: string, duration = 5000): string {
    return this.addToast('info', title, message, duration);
  }

  /**
   * Show a warning toast
   */
  warning(title: string, message: string, duration = 6000): string {
    return this.addToast('warning', title, message, duration);
  }

  /**
   * Show a notification toast (from backend)
   */
  notification(title: string, message: string, actionUrl?: string, duration = 8000): string {
    return this.addToast('notification', title, message, duration, actionUrl);
  }

  /**
   * Remove a specific toast
   */
  remove(id: string): void {
    const toasts = this.toastsSubject.value.filter((t) => t.id !== id);
    this.toastsSubject.next(toasts);
  }

  /**
   * Remove all toasts
   */
  removeAll(): void {
    this.toastsSubject.next([]);
  }

  /**
   * Get current toasts
   */
  getToasts(): Toast[] {
    return this.toastsSubject.value;
  }

  /**
   * Internal: add a toast and auto-remove if duration is set
   */
  private addToast(
    type: Toast['type'],
    title: string,
    message: string,
    duration?: number,
    actionUrl?: string,
  ): string {
    const id = `toast-${++this.toastIdCounter}`;
    const toast: Toast = {
      id,
      type,
      title,
      message,
      duration,
      actionUrl,
    };

    this.toastsSubject.next([...this.toastsSubject.value, toast]);

    // Auto-remove after duration if specified
    if (duration && duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }

    return id;
  }
}
